<script setup>
  import { computed, useSlots } from 'vue';

  const props = defineProps({
    icon: {
      type: String,
      default: () => '',
    },
    iconSubStyle: {
      type: String,
      default: () => 'fas',
    },
    type: {
      type: String,
      default: () => 'positive',
    },
    size: {
      type: String,
      default: () => 'medium',
    },
    filled: {
      type: Boolean,
      default: () => false,
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    isIcon: {
      type: Boolean,
      default: () => false,
    },
    noBorder: {
      type: Boolean,
      default: () => false,
    },
    isClear: {
      type: Boolean,
      default: () => false,
    },
    transparentBackground: {
      type: Boolean,
      default: () => false,
    },
  });

  const emit = defineEmits(['click', 'clearClick']);

  const slots = useSlots();

  const isBtnDisabled = computed(() => {
    return props.disabled || props.loading;
  });

  const isEmpty = () => !slots.default;

  const handleClick = (event) => {
    if (!isBtnDisabled.value) {
      emit('click', event);
    }
  };
  const handleClearClick = (event) => {
    if (!isBtnDisabled.value) {
      emit('clearClick', event);
    }
  };

</script>

<template>
  <div
    class="ui-button"
    :class="[
      `ui-button_${size}`,
      `ui-button_${type}`,
      {
        'ui-button_transparent': transparentBackground,
        'ui-button_is-icon': isIcon,
        'ui-button_disabled': isBtnDisabled,
        'ui-button_loading': loading,
        'ui-button_filled': filled,
        'ui-button_borderless': noBorder,
      },
    ]"
    @click="handleClick"
  >
    <div class="t:flex t:items-center t:space-x-4">
      <UiIcon
        v-if="icon"
        class="ui-button__icon"
        :name="icon"
        color="inherit"
        :substyle="iconSubStyle"
        :size="size === 'big' ? 14 : 12"
      />
      <span
        v-if="!isEmpty()"
        class="ui-button__text"
      >
        <slot />
      </span>
    </div>


    <div
      v-if="isClear"
      class="ui-button__clear"
      @click.stop="handleClearClick"
    >
      <UiIcon
        :size="8"
        name="times"
        color="#fff"
      />
    </div>
    <UiIcon
      v-if="loading"
      class="ui-button__loading"
      :size="18"
      color="inherit"
      name="spinner"
    />
  </div>
</template>

<style lang="scss">
.ui-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all .3s ease 0s;
  user-select: none;
  white-space: nowrap;
  border-width: 1px;
  border-style: solid;
  position: relative;
  font-weight: 700;
  border-radius: 5px;
  color: var(--text-body);
  background-color: white;

  &__text {
    line-height: 1;
    display: flex;
    align-items: center;
  }
  &__clear {
    width: 16px;
    height: 16px;
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: var(--text-negative);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
  }

  &_positive {
    border-color: var(--border-focused);
    &:hover {
      background-color: var(--background-brand-subtle);
    }
  }

  &_negative {
    color: var(--text-negative);
    border-color: var(--border-negative);
    &:hover {
      background-color: var(--background-negative-subtle);
    }
  }

  &_filled{
    color: var(--text-body-invert);
  }

  @at-root #{selector-append(& + '_borderless', & + '_positive')} {
    color: var(--background-primary);
  }
  @at-root #{selector-append(& + '_filled', & + '_positive')} {
    background-color: var(--background-primary);
    &:hover {
      background: linear-gradient(0deg, var(--overlay-black-20) 0%, var(--overlay-black-20) 100%), var(--background-primary);
    }
  }
  @at-root #{selector-append(& + '_filled', & + '_negative')} {
    background-color: var(--background-negative);
    &:hover {
      background: linear-gradient(0deg, var(--overlay-black-20) 0%, var(--overlay-black-20) 100%), var(--background-negative);
    }
  }

  &_big {
    font-size: 14px;
    padding: 9px 16px;
  }

  &_medium {
    font-size: 12px;
    padding: 8px 16px;
  }

  &_small {
    padding: 2px 6px;
    font-size: 10px;
  }

  @at-root #{selector-append(& + '_big', & + '_is-icon')} {
    padding: 9px 0;
    width: 34px;
    min-width: 34px;
  }
  @at-root #{selector-append(& + '_medium', & + '_is-icon')} {
    padding: 8px 0;
    width: 30px;
    min-width: 30px;
  }

  &_borderless {
    background-color: transparent;
    border-color: transparent;
    padding: 0;
    font-weight: 400;
    &:hover {
      background: transparent;
    }
  }

  &_disabled {
    cursor: not-allowed;
    filter: grayscale(100%);
    opacity: .5;
  }
  &_transparent {
    background-color: transparent;
  }

  &_loading {
    cursor: wait;
  }
  &_loading &{
    &__icon,
    &__text {
      opacity: 0;
    }
  }

  &__loading {
    position: absolute;
    animation: rotating 1.5s linear infinite;
  }
}
</style>
